import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faList } from '@fortawesome/free-solid-svg-icons';
import {
  fetchProducts,
  fetchProductById,
  deleteProduct,
  clearError,
  clearSuccessMessage
} from '../../../store/slices/inventorySlice';
import { toast } from 'react-toastify';
import InventoryProductModal from '../../../components/Inventory/InventoryProductModal';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import SearchBar from '../../../components/Common/SearchBar/SearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import EventLogSection from '../../../components/EventLogSection/EventLogSection';
import useEventLogModalScrollLock from '../../../hooks/useEventLogModalScrollLock';
import ConfirmDeleteModal from '../../../components/Common/ConfirmDeleteModal/ConfirmDeleteModal';
import '../../../components/Common/ListScreenPageLayout/listScreenPage.css';
import '../../PracticeManagement/SystemCaregiverManagement/SystemCaregiverManagement.css';
import './InventoryList.css';
import { matchesWordStartAny } from '../../../utils/searchMatch';

const InventoryList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clearBreadcrumbs } = useBreadcrumb();
  const { products, pagination, isListLoading, error, successMessage } = useSelector((state) => state.inventory);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState('10');
  const [showProductModal, setShowProductModal] = useState(false);
  const [productModalMode, setProductModalMode] = useState('add');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLogSection, setShowLogSection] = useState(false);
  const logOverlayRef = useRef(null);
  const logContainerRef = useRef(null);
  useEventLogModalScrollLock(showLogSection, logOverlayRef, logContainerRef);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearchTerm, searchTerm]);

  const listLimit = entriesPerPage === 'all' ? 9999 : parseInt(entriesPerPage, 10);

  useEffect(() => {
    dispatch(
      fetchProducts({
        page: currentPage,
        limit: listLimit,
        search: searchTerm
      })
    );
  }, [dispatch, currentPage, listLimit, searchTerm]);

  useEffect(() => {
    if (pagination?.total_pages && currentPage > pagination.total_pages) {
      setCurrentPage(Math.max(1, pagination.total_pages));
    }
  }, [pagination?.total_pages, currentPage]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearSuccessMessage());
    }
  }, [error, successMessage, dispatch]);

  const refetchList = useCallback(() => {
    dispatch(
      fetchProducts({
        page: currentPage,
        limit: listLimit,
        search: searchTerm
      })
    );
  }, [dispatch, currentPage, listLimit, searchTerm]);

  const totalPages = useMemo(() => {
    if (entriesPerPage === 'all') return 1;
    return Math.max(1, pagination?.total_pages ?? 1);
  }, [entriesPerPage, pagination?.total_pages]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handleViewProduct = useCallback(
    (productId) => {
      navigate(`/inventory/${productId}`);
    },
    [navigate]
  );

  const handleEditProduct = useCallback(
    async (product) => {
      try {
        const detailed = await dispatch(fetchProductById(product.id)).unwrap();
        setSelectedProduct(detailed?.data || product);
      } catch {
        // Fallback to row data if detail fetch fails.
        setSelectedProduct(product);
      }
      setProductModalMode('edit');
      setShowProductModal(true);
    },
    [dispatch]
  );

  const handleDeleteProduct = async () => {
    if (!selectedProduct?.id) return;
    setIsDeletingProduct(true);
    try {
      await dispatch(deleteProduct(selectedProduct.id)).unwrap();
      toast.success('Product deleted successfully');
      setShowDeleteModal(false);
      setSelectedProduct(null);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : err?.message || 'Failed to delete product');
    } finally {
      setIsDeletingProduct(false);
    }
  };

  const handleOpenDeleteModal = useCallback((product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  }, []);

  const handleCloseDeleteModal = () => {
    if (isDeletingProduct) return;
    setShowDeleteModal(false);
    setSelectedProduct(null);
  };

  const handleCloseProductModal = (detail) => {
    setShowProductModal(false);
    setProductModalMode('add');
    if (detail?.saved) {
      refetchList();
    }
    setSelectedProduct(null);
  };

  const handleSort = useCallback(
    (key) => {
      if (sortBy === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      else {
        setSortBy(key);
        setSortOrder('asc');
      }
    },
    [sortBy]
  );

  const filteredProducts = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    const q = String(searchTerm || '').trim();
    if (!q) return list;
    return list.filter((item) =>
      matchesWordStartAny(
        [
          item.product_name,
          item.product_category,
          item.model_number,
          item.status,
          item.price,
          item.received_date,
        ],
        q
      )
    );
  }, [products, searchTerm]);

  const sortedProducts = useMemo(() => {
    const list = filteredProducts;
    if (!sortBy) return list;

    const statusRank = (s) => {
      const t = String(s ?? '').toLowerCase();
      if (t === 'active' || t === 'a' || t === '1') return 0;
      return 1;
    };

    return [...list].sort((a, b) => {
      if (sortBy === 'status') {
        const primary = statusRank(a?.status) - statusRank(b?.status);
        if (primary !== 0) return sortOrder === 'asc' ? primary : -primary;
        return (Number(a?.id) || 0) - (Number(b?.id) || 0);
      }

      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      if (aValue === null || aValue === undefined || aValue === 'N/A') aValue = '';
      if (bValue === null || bValue === undefined || bValue === 'N/A') bValue = '';

      if (sortBy === 'id' || sortBy === 'qty') {
        aValue = parseInt(aValue, 10) || 0;
        bValue = parseInt(bValue, 10) || 0;
      }

      if (sortBy === 'price') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      let cmp = 0;
      if (aValue < bValue) cmp = -1;
      else if (aValue > bValue) cmp = 1;
      const ordered = sortOrder === 'asc' ? cmp : -cmp;
      if (ordered !== 0) return ordered;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    });
  }, [filteredProducts, sortBy, sortOrder]);

  const columns = useMemo(
    () => [
      {
        key: 'id',
        title: '#',
        sortable: true,
        width: '56px',
        className: 'reusable-table-centered-col inventory-index-col',
        render: (row) => row.id
      },
      {
        key: 'product_name',
        title: 'Product Name',
        sortable: true,
        width: '18%',
        render: (row) => (
          <button
            type="button"
            className="inventory-list-name-link"
            onClick={() => handleViewProduct(row.id)}
          >
            {row.product_name || 'N/A'}
          </button>
        )
      },
      {
        key: 'product_category',
        title: 'Product Category',
        sortable: true,
        width: '18%',
        render: (row) => row.product_category || 'N/A'
      },
      { key: 'model_number', title: 'Model Number', sortable: true, width: '15%', render: (row) => row.model_number || 'N/A' },
      { key: 'received_date', title: 'Received Date', sortable: true, width: '14%', render: (row) => row.received_date || 'N/A' },
      {
        key: 'price',
        title: 'Price',
        sortable: true,
        width: '10%',
        className: 'reusable-table-centered-col',
        render: (row) => `$${parseFloat(row.price || 0).toFixed(2)}`
      },
      {
        key: 'qty',
        title: 'QTY',
        sortable: true,
        width: '8%',
        className: 'reusable-table-centered-col',
        render: (row) => row.qty ?? 'N/A'
      },
      {
        key: 'status',
        title: 'Status',
        sortable: true,
        width: '96px',
        className: 'reusable-table-centered-col',
        render: (row) => <StatusBadge status={row.status || 'N/A'} />
      },
      {
        key: 'action',
        title: 'Action',
        width: '130px',
        className: 'reusable-table-centered-col',
        render: (row) => (
          <ActionButtons
            onView={() => handleViewProduct(row.id)}
            onEdit={() => handleEditProduct(row)}
            onDelete={() => handleOpenDeleteModal(row)}
            isDeleting={false}
          />
        )
      }
    ],
    [handleViewProduct, handleEditProduct, handleOpenDeleteModal]
  );

  const entriesSelectValue = entriesPerPage === 'all' ? 'all' : String(entriesPerPage);

  return (
    <div className="inventory-list-feature">
      <div className="list-screen-page-container scm-page">
        <div className="scm-page-header">
          <h2 className="dashboard-title">Inventory List</h2>
        </div>
        <div className="scm-list-frame">
          <div className="scm-list-panel">
            <div className="scm-list-body">
              <div className="scm-header-row">
                <div className="scm-header-actions">
                  <SearchBar
                    value={localSearchTerm}
                    onChange={setLocalSearchTerm}
                    placeholder="Search products..."
                    label={null}
                    className="scm-search-wrapper"
                  />
                  <button
                    type="button"
                    className="scm-add-btn"
                    onClick={() => {
                      setSelectedProduct(null);
                      setProductModalMode('add');
                      setShowProductModal(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="scm-add-icon" />
                    Add Inventory
                  </button>
                </div>
              </div>

              <div className="practice-tab-table-card">
                <Table
                  columns={columns}
                  data={sortedProducts}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  isLoading={isListLoading}
                  loadingMode="skeleton"
                  skeletonRows={10}
                  emptyMessage="No products found"
                />

                <div className="scm-list-footer">
                  <ListScreenPaginationBar
                    entriesValue={entriesSelectValue}
                    onEntriesChange={handleEntriesChange}
                    currentPage={pagination?.current_page ?? currentPage}
                    totalRecords={pagination?.total ?? sortedProducts.length}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="inventory-list-event-log-actions">
          <button
            type="button"
            className="scm-events-log-btn"
            onClick={() => setShowLogSection(!showLogSection)}
          >
            <FontAwesomeIcon icon={faList} className="list-screen-me-2" />
            Event Logs
          </button>
        </div>

        {showLogSection && (
          <div ref={logOverlayRef} className="inventory-list-modal-overlay">
            <div
              ref={logContainerRef}
              className="inventory-list-modal-content inventory-list-event-logs-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="inventory-list-modal-header">
                <h5 className="inventory-list-modal-title">Inventory Event Logs</h5>
                <button type="button" className="inventory-list-modal-close" onClick={() => setShowLogSection(false)}>
                  ×
                </button>
              </div>
              <div className="inventory-list-modal-body inventory-list-event-logs-body">
                <EventLogSection moduleId="Inventory" />
              </div>
            </div>
          </div>
        )}

        <InventoryProductModal
          show={showProductModal}
          onClose={handleCloseProductModal}
          mode={productModalMode}
          product={productModalMode === 'edit' ? selectedProduct : null}
        />

        <ConfirmDeleteModal
          show={showDeleteModal}
          onCancel={handleCloseDeleteModal}
          onConfirm={handleDeleteProduct}
          isDeleting={isDeletingProduct}
          message={
            <p>
              Are you sure you want to delete the product{' '}
              <strong>
                {selectedProduct?.product_name || 'this product'}
                {selectedProduct?.model_number ? ` (${selectedProduct.model_number})` : ''}
              </strong>
              ?
            </p>
          }
        />
      </div>
    </div>
  );
};

export default InventoryList;
